import React, { useEffect, useState } from "react";
import {
  getProfileInitials,
} from "../utils/profileAvatar";
import {
  hasUsableAvatarIdentity,
  unresolvedAvatarInitialsFallback,
} from "../auth/profileIdentity";

/**
 * SA Account Profile–parity image circle for MA Account.
 * Used for BOTH the personal avatar (My Profile) and the company logo
 * (Company Profile) — parent owns upload/persistence via the canonical APIs.
 */
export default function ProfileAvatarCircle({
  editMode = false,
  displayUrl = null,
  firstName = "",
  lastName = "",
  email = "",
  uploading = false,
  identityLoading = false,
  dragActive = false,
  rootProps = {},
  inputProps = {},
  onRemove = null,
  onImageError = null,
  testIdPrefix = "ma-profile-avatar",
  imageAlt = "Profile",
  uploadLabel = "Upload Photo",
  removeLabel = "Remove photo",
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const identityReady = hasUsableAvatarIdentity({ firstName, lastName, email });
  const resolvedInitials = getProfileInitials({ firstName, lastName, email });
  const initials =
    resolvedInitials ||
    (!identityLoading ? unresolvedAvatarInitialsFallback() : "");
  const showImage = Boolean(displayUrl) && !imageFailed && !uploading;
  const showLoadingPlaceholder =
    identityLoading && !showImage && !uploading && !editMode;

  useEffect(() => {
    setImageFailed(false);
  }, [displayUrl]);

  const handleImgError = () => {
    setImageFailed(true);
    if (typeof onImageError === "function") onImageError();
  };

  const className = [
    "profile-avatar-circle",
    editMode ? "profile-avatar-circle--editable" : "",
    dragActive ? "profile-avatar-circle--drag-active" : "",
    showLoadingPlaceholder ? "profile-avatar-circle--loading" : "",
  ]
    .filter(Boolean)
    .join(" ");

  let inner;
  if (uploading) {
    inner = (
      <span
        className="profile-avatar-circle__upload-text"
        data-testid={`${testIdPrefix}-uploading`}
      >
        Uploading...
      </span>
    );
  } else if (showImage) {
    inner = (
      <img
        src={displayUrl}
        alt={imageAlt}
        className="profile-avatar-circle__image"
        data-testid={`${testIdPrefix}-image`}
        onError={handleImgError}
      />
    );
  } else if (editMode) {
    inner = (
      <span
        className="profile-avatar-circle__upload-text"
        data-testid={`${testIdPrefix}-upload-affordance`}
      >
        {uploadLabel}
      </span>
    );
  } else if (showLoadingPlaceholder || (identityLoading && !identityReady)) {
    inner = (
      <span
        className="profile-avatar-circle__loading"
        data-testid={`${testIdPrefix}-loading`}
        aria-hidden="true"
      />
    );
  } else {
    inner = (
      <span
        className="profile-avatar-circle__initials"
        data-testid={`${testIdPrefix}-initials`}
        aria-hidden="true"
      >
        {initials}
      </span>
    );
  }

  return (
    <div className="profile-avatar-wrap" data-testid={`${testIdPrefix}-wrap`}>
      <div
        className={className}
        data-testid={testIdPrefix}
        aria-busy={showLoadingPlaceholder || undefined}
        {...(editMode ? rootProps : {})}
      >
        {editMode ? (
          <input {...inputProps} data-testid={`${testIdPrefix}-file-input`} />
        ) : null}
        {inner}
      </div>
      {editMode && showImage && typeof onRemove === "function" ? (
        <button
          type="button"
          className="profile-avatar-remove"
          data-testid={`${testIdPrefix}-remove`}
          aria-label={removeLabel}
          title={removeLabel}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
